import { ArrowLeft, Building2, Plus, Trash2, User } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
    CompanyFormData, 
    IFounder, 
    IContact, 
    CompanySize, 
    FundingStage
} from "../../../interfaces/company";
import {
    getAdminCompany,
    createAdminCompany,
    updateAdminCompany,
} from "../../../interfaces/admin/companies";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminCompanyForm() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    
    const [company, setCompany] = useState<CompanyFormData>({
        name: "",
        siren: "",
        status: "prospect",
        priority: "medium",
        founders: [],
        contacts: [],
        tags: {
            franchise: "",
            categories: [],
            subCategories: [],
        },
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        if (isEdit && id) {
            fetchCompany(id);
        }
    }, [isEdit, id]);

    const fetchCompany = async (companyId: string) => {
        try {
            setLoading(true);
            const data = await getAdminCompany(companyId);
            setCompany({
                name: data.name,
                imageUrl: data.imageUrl || "",
                website: data.website || "",
                siren: data.siren,
                description: data.description || "",
                industry: data.industry || "",
                location: data.location || "",
                size: data.size,
                fundingStage: data.fundingStage,
                founders: data.founders || [],
                contacts: data.contacts || [],
                status: data.status || "prospect",
                tags: data.tags || {
                    franchise: "",
                    categories: [],
                    subCategories: [],
                },
                notes: data.notes || "",
                priority: data.priority || "medium",
                lastContactAt: data.lastContactAt ? data.lastContactAt.split('T')[0] : "",
                nextAction: data.nextAction || "",
                nextActionAt: data.nextActionAt ? data.nextActionAt.split('T')[0] : "",
            });
        } catch (err) {
            console.error("Failed to fetch company:", err);
            setError("Failed to load company");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!company.name.trim() || !company.siren.trim()) {
            setError("Company name and SIREN are required");
            return;
        }

        try {
            setSubmitLoading(true);
            setError(null);
            
            if (isEdit && id) {
                await updateAdminCompany(id, company);
            } else {
                await createAdminCompany(company);
            }
            
            navigate("/bo/companies");
        } catch (err: any) {
            console.error("Failed to save company:", err);
            setError(err.response?.data?.error || "Failed to save company");
        } finally {
            setSubmitLoading(false);
        }
    };

    const addFounder = () => {
        setCompany(prev => ({
            ...prev,
            founders: [...(prev.founders || []), {}]
        }));
    };

    const updateFounder = (index: number, field: keyof IFounder, value: string) => {
        setCompany(prev => ({
            ...prev,
            founders: prev.founders?.map((founder, i) => 
                i === index ? { ...founder, [field]: value } : founder
            ) || []
        }));
    };

    const removeFounder = (index: number) => {
        setCompany(prev => ({
            ...prev,
            founders: prev.founders?.filter((_, i) => i !== index) || []
        }));
    };

    const addContact = () => {
        setCompany(prev => ({
            ...prev,
            contacts: [...(prev.contacts || []), { isPrimary: (prev.contacts?.length || 0) === 0 }]
        }));
    };

    const updateContact = (index: number, field: keyof IContact, value: string | boolean) => {
        setCompany(prev => ({
            ...prev,
            contacts: prev.contacts?.map((contact, i) => {
                if (i === index) {
                    const updated = { ...contact, [field]: value };
                    // If setting this contact as primary, unset others
                    if (field === 'isPrimary' && value === true) {
                        return updated;
                    }
                    return updated;
                } else if (field === 'isPrimary' && value === true) {
                    // Unset isPrimary for other contacts
                    return { ...contact, isPrimary: false };
                }
                return contact;
            }) || []
        }));
    };

    const removeContact = (index: number) => {
        setCompany(prev => ({
            ...prev,
            contacts: prev.contacts?.filter((_, i) => i !== index) || []
        }));
    };

    const sizeOptions: { value: CompanySize; label: string }[] = [
        { value: "1-10", label: "1-10 employees" },
        { value: "11-50", label: "11-50 employees" },
        { value: "51-200", label: "51-200 employees" },
        { value: "201-500", label: "201-500 employees" },
        { value: "500+", label: "500+ employees" },
        { value: "1000+", label: "1000+ employees" },
    ];

    const fundingStageOptions: { value: FundingStage; label: string }[] = [
        { value: "bootstrap", label: "Bootstrap" },
        { value: "seed", label: "Seed" },
        { value: "series_a", label: "Series A" },
        { value: "series_b", label: "Series B" },
        { value: "series_c", label: "Series C" },
        { value: "series_d", label: "Series D" },
        { value: "ipo", label: "IPO" },
        { value: "acquired", label: "Acquired" },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F0EB]">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">Loading company...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-4xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate("/bo/companies")}
                            className="p-2 hover:bg-gray-100 rounded-xs transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">
                                {isEdit ? "Edit Company" : "Create Company"}
                            </h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                {isEdit ? "Update company information" : "Add a new company to the system"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <BONavigation />

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xs p-3">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Basic Information */}
                    <div className="bg-white border border-gray-200 rounded-xs p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            Basic Information
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    value={company.name}
                                    onChange={(e) => setCompany(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    SIREN *
                                </label>
                                <input
                                    type="text"
                                    value={company.siren}
                                    onChange={(e) => setCompany(prev => ({ ...prev, siren: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm font-mono"
                                    placeholder="123456789"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    value={company.website}
                                    onChange={(e) => setCompany(prev => ({ ...prev, website: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    placeholder="https://example.com"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Logo URL
                                </label>
                                <input
                                    type="url"
                                    value={company.imageUrl}
                                    onChange={(e) => setCompany(prev => ({ ...prev, imageUrl: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    placeholder="https://example.com/logo.png"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Industry
                                </label>
                                <input
                                    type="text"
                                    value={company.industry}
                                    onChange={(e) => setCompany(prev => ({ ...prev, industry: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    placeholder="e.g., Technology, Healthcare"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    value={company.location}
                                    onChange={(e) => setCompany(prev => ({ ...prev, location: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    placeholder="e.g., Paris, France"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company Size
                                </label>
                                <select
                                    value={company.size || ""}
                                    onChange={(e) => setCompany(prev => ({ ...prev, size: e.target.value as CompanySize || undefined }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm bg-white"
                                >
                                    <option value="">Select size</option>
                                    {sizeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Funding Stage
                                </label>
                                <select
                                    value={company.fundingStage || ""}
                                    onChange={(e) => setCompany(prev => ({ ...prev, fundingStage: e.target.value as FundingStage || undefined }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm bg-white"
                                >
                                    <option value="">Select stage</option>
                                    {fundingStageOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={company.description}
                                onChange={(e) => setCompany(prev => ({ ...prev, description: e.target.value }))}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm resize-none"
                                placeholder="Brief description of the company..."
                            />
                        </div>
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes
                            </label>
                            <textarea
                                value={company.notes}
                                onChange={(e) => setCompany(prev => ({ ...prev, notes: e.target.value }))}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm resize-none"
                                placeholder="Internal notes about the company..."
                            />
                        </div>
                    </div>

                    {/* Founders */}
                    <div className="bg-white border border-gray-200 rounded-xs p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Founders
                            </h2>
                            <button
                                type="button"
                                onClick={addFounder}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-xs transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Add Founder
                            </button>
                        </div>
                        
                        {company.founders?.map((founder, index) => (
                            <div key={index} className="border border-gray-200 rounded-xs p-4 mb-3">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-medium text-gray-900">Founder {index + 1}</h3>
                                    <button
                                        type="button"
                                        onClick={() => removeFounder(index)}
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <input
                                        type="text"
                                        placeholder="First name"
                                        value={founder.firstName || ""}
                                        onChange={(e) => updateFounder(index, "firstName", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Last name"
                                        value={founder.lastName || ""}
                                        onChange={(e) => updateFounder(index, "lastName", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={founder.title || ""}
                                        onChange={(e) => updateFounder(index, "title", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={founder.email || ""}
                                        onChange={(e) => updateFounder(index, "email", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="LinkedIn URL"
                                        value={founder.linkedinUrl || ""}
                                        onChange={(e) => updateFounder(index, "linkedinUrl", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Phone"
                                        value={founder.phone || ""}
                                        onChange={(e) => updateFounder(index, "phone", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                </div>
                            </div>
                        ))}
                        
                        {company.founders?.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">
                                No founders added yet. Click "Add Founder" to get started.
                            </p>
                        )}
                    </div>

                    {/* Contacts */}
                    <div className="bg-white border border-gray-200 rounded-xs p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Contacts
                            </h2>
                            <button
                                type="button"
                                onClick={addContact}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-xs transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Add Contact
                            </button>
                        </div>
                        
                        {company.contacts?.map((contact, index) => (
                            <div key={index} className="border border-gray-200 rounded-xs p-4 mb-3">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-sm font-medium text-gray-900">Contact {index + 1}</h3>
                                        <label className="flex items-center gap-1 text-xs">
                                            <input
                                                type="checkbox"
                                                checked={contact.isPrimary || false}
                                                onChange={(e) => updateContact(index, "isPrimary", e.target.checked)}
                                                className="rounded border-gray-200"
                                            />
                                            Primary Contact
                                        </label>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeContact(index)}
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <input
                                        type="text"
                                        placeholder="First name"
                                        value={contact.firstName || ""}
                                        onChange={(e) => updateContact(index, "firstName", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Last name"
                                        value={contact.lastName || ""}
                                        onChange={(e) => updateContact(index, "lastName", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={contact.title || ""}
                                        onChange={(e) => updateContact(index, "title", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={contact.email || ""}
                                        onChange={(e) => updateContact(index, "email", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="LinkedIn URL"
                                        value={contact.linkedinUrl || ""}
                                        onChange={(e) => updateContact(index, "linkedinUrl", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                    <input
                                        type="tel"
                                        placeholder="Phone"
                                        value={contact.phone || ""}
                                        onChange={(e) => updateContact(index, "phone", e.target.value)}
                                        className="px-3 py-2 border border-gray-200 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none text-sm"
                                    />
                                </div>
                            </div>
                        ))}
                        
                        {company.contacts?.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">
                                No contacts added yet. Click "Add Contact" to get started.
                            </p>
                        )}
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/bo/companies")}
                            className="px-4 py-2 text-sm border border-gray-200 rounded-xs hover:bg-[#F9F6F3] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitLoading}
                            className="px-4 py-2 text-sm bg-black text-white rounded-xs hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                        >
                            {submitLoading ? "Saving..." : isEdit ? "Update Company" : "Create Company"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}